// SPDX-FileCopyrightText: Copyright 2025 OP Financial Group (https://op.fi). All Rights Reserved.
//
// SPDX-License-Identifier: LicenseRef-OpAllRightsReserved

import { createContext } from 'react';
import { AgreementTypes } from '../Constants';
import { roles } from './Roles';

// =============================================================================
// CORE DOMAIN INTERFACES
// =============================================================================

export interface Role {
    name: string;
    isChecked: boolean;
    roleGroup: string[];
    heading: string;
    selectedForAgreementType?: string;
}

export interface AdminAgreementType {
    agreementType?: string;
    roles: Role[];
    allRolesSelected?: boolean;
}

export interface AdminRolesData {
    rolesData: AdminAgreementType;
}

// =============================================================================
// ADMIN INTERFACES
// =============================================================================

export interface AdminData {
    id?: string;
    firstName: string;
    lastName: string;
    personalIdentityCode: string;
    adminPhoneNumber: string;
    adminEmailAddress: string;
    canEditRoles?: boolean;
    agreementTypes: Record<string, AdminRolesData>;
}

// =============================================================================
// ADMIN BACKEND INTERFACES
// =============================================================================

export interface AdminDataBackend {
    id?: string;
    firstName: string;
    lastName: string;
    personalIdentityCode: string;
    adminPhoneNumber: string;
    adminEmailAddress: string;
    canEditRoles?: boolean;
    adminAgreementTypes: AdminAgreementType[];
}

// =============================================================================
// ADDITIONAL SIGNATORY INTERFACES
// =============================================================================

export interface AdditionalSignatoryData {
    id?: string;
    firstName: string;
    lastName: string;
    phoneNumber: string;
    emailAddress: string;
}

// =============================================================================
// COMPANY INTERFACES
// =============================================================================

export interface CompanyTypeSelected {
    isSelected: boolean;
    showWarning?: boolean;
}

export interface CompanyData {
    agreementType: string;
    businessId: string;
    companyName: string;
    streetAddress?: string;
    postalCode?: string;
    cityTown?: string;
    phone?: string;
    isForeignCompany?: boolean;
    country?: string;
    companyTypes: string[];
    companyTypeMessages?: string[];
    administrators: AdminData[];
    acceptTerms: boolean;
}

// =============================================================================
// COMPANY BACKEND INTERFACES
// =============================================================================

export interface CompanyDataBackend {
    agreementType: string;
    businessId: string;
    companyName: string;
    streetAddress?: string;
    postalCode?: string;
    cityTown?: string;
    phone?: string;
    isForeignCompany?: boolean;
    country?: string;
    companyTypes: string[];
    companyTypeMessages?: string[];
    administrators: AdminDataBackend[];
    acceptTerms: boolean;
    additionalSignatories?: AdditionalSignatoryData[];
    additionalSignatorySelection?: string;
}

// =============================================================================
// CONTRACT & SIGNING INTERFACES
// =============================================================================

export interface ConfirmSignatory {
    acceptTerms: boolean;
    selectedSigning: string;
}

export interface ContractData {
    companyData: CompanyData;
    administrators: AdminData[];
    additionalSignatories: AdditionalSignatoryData[];
    additionalSignatorySelection: string;
    showAgreementTypes: boolean;
    checkValidation: boolean;
    confirmSignatory: ConfirmSignatory;
    companyTypeSelected: CompanyTypeSelected;
}

// =============================================================================
// CONTEXT INTERFACE
// =============================================================================

export interface ContextWrapperType {
    isAdminFlow: boolean;
    contractData: ContractData;
    updateInformation: any;
    isDataSavingInProgress: boolean;
    setIsDataSavingInProgress: any;
}

// =============================================================================
// API INTERFACES
// =============================================================================

export interface ApiResponse {
    status: string;
    EMPTY?: boolean;
    body?: string;
}

// =============================================================================
// DEFAULT DATA & TEMPLATES
// =============================================================================

const companyData: CompanyData = {
    businessId: '',
    companyName: '',
    streetAddress: '',
    postalCode: '',
    cityTown: '',
    phone: '',
    companyTypes: [AgreementTypes.COMPANY],
    isForeignCompany: false,
    country: '',
    acceptTerms: false,
    administrators: [],
    agreementType: AgreementTypes.COMPANY,
};

export const additionalSignatoryTemplate = (): AdditionalSignatoryData => ({
    id: Date.now().toString() + Math.random().toString(36).substr(2, 9), // NOSONAR
    firstName: '',
    lastName: '',
    phoneNumber: '',
    emailAddress: '',
});

export const adminDataTemplate = (showAgreementTypes: boolean = false): AdminData => ({
    id: Date.now().toString() + Math.random().toString(36).substr(2, 9), // NOSONAR
    firstName: '',
    lastName: '',
    personalIdentityCode: '',
    adminPhoneNumber: '',
    adminEmailAddress: '',
    canEditRoles: true,
    agreementTypes: showAgreementTypes
        ? {}
        : {
              company: {
                  rolesData: {
                      agreementType: AgreementTypes.COMPANY,
                      roles: roles(true),
                      allRolesSelected: true,
                  },
              },
          },
});

const confirmSignatory: ConfirmSignatory = {
    acceptTerms: false,
    selectedSigning: 'tupas',
};

const companyTypeSelected: CompanyTypeSelected = {
    isSelected: false,
    showWarning: false,
};

export const defaultContractData: ContractData = {
    companyData: companyData,
    administrators: [adminDataTemplate()],
    additionalSignatories: [],
    additionalSignatorySelection: '',
    showAgreementTypes: false,
    checkValidation: false,
    confirmSignatory: confirmSignatory,
    companyTypeSelected: companyTypeSelected,
};

export interface PdfState {
    isLoading: boolean;
    error: boolean;
    errorItems?: string[];
}

// =============================================================================
// CONTEXT CREATION
// =============================================================================

export const ContextWrapper = createContext<ContextWrapperType>({
    isAdminFlow: false,
    contractData: defaultContractData,
    updateInformation: () => {},
    isDataSavingInProgress: false,
    setIsDataSavingInProgress: () => {},
});
